import * as React from 'react';
import { DataGrid, GridRowModes, GridRowModesModel, GridActionsCellItem, GridRowEditStopReasons } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import {
  Box, Button, IconButton, Menu, MenuItem, Pagination, PaginationItem, Stack, Tooltip
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutofpsSelectIcon from '@mui/icons-material/AutofpsSelect';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import TableChartIcon from '@mui/icons-material/TableChart';
import SlideshowIcon from '@mui/icons-material/Slideshow';

const EditTableView = React.forwardRef(function EditTableView({
  rows: propRows, columns: propColumns, checkboxSelection, getRowId,
  backBtn, nextBtn, handleNext, handleBack, handleRowClick,
  hoverTable, hoverTableOptions, hoverActionFuncHandle,
  totalPage, paginationCount, handlePagination, totalRecord,
  getRowClassName, tableName, highLightedRow,
  onRowUpdate,
  onBatchRowUpdate, // <-- new prop for batch update
  fieldDefinitions, // <-- new prop, pass the field definitions array here
},ref) {
  // Row editing handlers
  const [rows, setRows] = React.useState(propRows);
  const rowsRef = React.useRef(propRows);
  const [rowModesModel, setRowModesModel] = React.useState({});
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [editedRows, setEditedRows] = React.useState({});


  const sanitizeRow = (row) => {
    const cleaned = { ...row };
    Object.keys(cleaned).forEach(key => {
      if (cleaned[key] === null || cleaned[key] === undefined) {
        cleaned[key] = "";   // MUI-safe default for text / select / singleSelect
      }
    });
    return cleaned;
  };
  
  // Helper function to get row ID - same logic as DataGrid's getRowId
  const getRowIdFromRow = React.useCallback((row) => {
    if (!row) return null;
    if (getRowId && typeof getRowId === 'function') {
      try {
        const id = getRowId(row);
        if (id != null) return id;
      } catch (e) {
        console.warn("getRowId function error:", e);
      }
    }
    // Universal fallback - check all possible ID fields
    return row.case_id ?? 
           row.progress_id ?? 
           row.property_id ?? 
           row.fir_id ?? 
           row._id ?? 
           (row.id != null ? row.id : null) ??  // Check if id exists and is not null
           row.sl_no ??
           row.unique_id ??
           null;
  }, [getRowId]);

  // Sync local rows with propRows if changed externally
  React.useEffect(() => {
    if (!propRows) return;
  
    const normalizedRows = propRows.map((row, index) => {
      const clean = sanitizeRow(row);
      
      // Get the actual ID using the same logic as DataGrid
      const rowId = getRowIdFromRow(clean);
      
      // Ensure the row has an id field set (DataGrid needs this)
      return {
        ...clean,
        id: rowId ?? `row-${index + 1}`,  // fallback ID if none found
        // Also ensure the primary ID field is set for consistency
        ...(rowId && !clean.id ? { id: rowId } : {}),
      };
    });
  
    setRows(normalizedRows);
    rowsRef.current = normalizedRows; // Update ref
  
    // Clean up rowModesModel to only include current rows
    // This prevents errors when pagination changes the row set
    const modes = {};
    normalizedRows.forEach((r) => {
      const rId = getRowIdFromRow(r) || r.id;
      if (rId) {
        modes[rId] = { mode: GridRowModes.Edit };
      }
    });
  
    setRowModesModel(modes);
  }, [propRows, getRowIdFromRow]);
  
    

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow((prev) => (prev?.id === row.id ? null : row));
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleHoverOptionClick = (option) => {
    if (option && typeof option.onclick === "function") {
      option.onclick(selectedRow);
    } else {
      hoverActionFuncHandle(option, selectedRow);
    }
    handleMenuClose();
  };

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id) => async () => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    try {
      const updatedRow = await processRowUpdate(row);
      setRows((prev) => prev.map((r) => (r.id === id ? updatedRow : r)));

      setRowModesModel((prevModel) => ({
        ...prevModel,
        [id]: { mode: GridRowModes.Edit },
      }));
    } catch (error) {
      console.error("Save failed:", error);
    }
  };


  

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
    const editedRow = rows.find((row) => row.id === id);
    if (editedRow && editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  // Build a map for quick lookup by field name
  const fieldDefMap = React.useMemo(() => {
    const map = {};
    if (Array.isArray(fieldDefinitions)) {
      fieldDefinitions.forEach(f => { map[f.name] = f; });
    }
    return map;
  }, [fieldDefinitions]);

  // Helper to get valueOptions for a column from fieldDefinitions
  const getValueOptions = (col) => {
    const fieldDef = fieldDefMap[col.field];
    if (
      (fieldDef && (fieldDef.type === 'dropdown' || fieldDef.type === 'autocomplete') && Array.isArray(fieldDef.options))
    ) {
      return fieldDef.options;
    }
    if (col.valueOptions) {
      return col.valueOptions;
    }
    if (col.type === 'autocomplete' && Array.isArray(col.options)) {
      return col.options;
    }

    return undefined;
  };

  // Helper to get type for a column from fieldDefinitions
  const getColType = (col) => {
    const fieldDef = fieldDefMap[col.field];
    if (!fieldDef) return col.type;
    if (fieldDef.type === 'dropdown' || fieldDef.type === 'autocomplete') return 'autocomplete';
    if (fieldDef.type === 'file' || fieldDef.type === 'profilepicture') return 'file';
    if (fieldDef.type === 'date') return 'date';
    if (fieldDef.type === 'number') return 'number';
    return col.type;
  };

  // Helper to get dropdown value from label (for saving)
  const getDropdownValue = (col, valueOrLabel) => {
    const options = getValueOptions(col);
    if (!options) return valueOrLabel;
    if (typeof options[0] === 'object') {
      const found = options.find(opt => opt.label === valueOrLabel || opt.value === valueOrLabel);
      return found ? found.value : valueOrLabel;
    }
    return valueOrLabel;
  };

  const getFileIcon = (fileName) => {
    if (!fileName || typeof fileName !== "string") return <InsertDriveFileIcon />;

    const extension = fileName.split(".").pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon color="error" />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'svg':
      case 'gif':
        return <ImageIcon color="primary" />;
      case 'xls':
      case 'xlsx':
        return <TableChartIcon color="success" />;
      case 'csv':
      case 'doc':
      case 'docx':
        return <DescriptionIcon />;
      case 'ppt':
        return <SlideshowIcon color="warning" />;
      default:
        return <InsertDriveFileIcon />;
    }
  };


  // Actions column definition
  // const actionsColumn = {
  //   field: 'actions',
  //   type: 'actions',
  //   headerName: 'Actions',
  //   width: 100,
  //   cellClassName: 'actions',
  //   getActions: ({ id }) => {
  //     const isInEditMode = rowModesModel[id]?.mode === 'edit';
  //     if (isInEditMode) {
  //       return [
  //         // <GridActionsCellItem
  //         //   icon={<SaveIcon />}
  //         //   label="Save"
  //         //   onClick={handleSaveClick(id)}
  //         //   color="primary"
  //         // />,
  //         // <GridActionsCellItem
  //         //   icon={<CancelIcon />}
  //         //   label="Cancel"
  //         //   onClick={handleCancelClick(id)}
  //         //   color="inherit"
  //         // />,
  //       ];
  //     }
  //     return [
  //       // <GridActionsCellItem
  //       //   icon={<EditIcon />}
  //       //   label="Edit"
  //       //   onClick={handleEditClick(id)}
  //       //   color="inherit"
  //       // />,
  //     ];
  //   },
  // };

  // Compose columns: actions + propColumns (editable, with type/options from fieldDefinitions)
  const updatedColumns = [
    // actionsColumn,
    ...propColumns.map(col => {
      // Make S.No column non-editable
      if (col.field === 'sl_no') {
        return { ...col, editable: false };
      }
      const colType = getColType(col);
      if (
        ((colType === 'autocomplete' || colType === 'singleSelect') && getValueOptions(col)) ||
        (fieldDefMap[col.field] && (fieldDefMap[col.field].type === 'autocomplete' || fieldDefMap[col.field].type === 'dropdown'))
      ) {
        const options = getValueOptions(col);
        return {
          ...col,
          editable: true,
          type: 'singleSelect',
          valueOptions: options,
          renderEditCell: undefined,
          valueFormatter: (params) => {
            if (!params || params.value == null) return '';
            const opts = getValueOptions(col);
            if (!opts) return params.value;

            if (Array.isArray(opts) && typeof opts[0] === 'object') {
              const found = opts.find(opt =>
                opt != null && (opt.value === params.value || opt.label === params.value)
              );
              return found?.label ?? params.value;
            }

            return params.value;
          },
        };
      }
      if (colType === 'file') {
        return {
          ...col,
          editable: false,
          renderCell: (params) => {
            const file = params.value;
            const fileName = typeof file === 'string'
              ? file.split('/').pop()
              : file?.name || '';

            return (
              <Tooltip title={fileName || "Upload File"}>
                <IconButton
                  size="small"
                  component="label"
                  onClick={e => e.stopPropagation()}
                >
                  {file ? (
                    <>
                      <AttachFileIcon fontSize="small" sx={{ mr: 0.5 }} />
                      <span style={{ fontSize: "12px", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '80px' }}>
                        {fileName}
                      </span>
                    </>
                  ) : (
                    <AddCircleOutlineIcon color="primary" />
                  )}
                  <input
                    type="file"
                    hidden
                    onChange={event => {
                      const newFile = event.target.files[0];
                      if (newFile) {
                        const rowId = params.id;
                        setRowModesModel(prev => ({
                          ...prev,
                          [rowId]: { mode: GridRowModes.Edit }
                        }));
                        setRows(prevRows =>
                          prevRows.map(row =>
                            row.id === rowId
                              ? { ...row, [col.field]: newFile }
                              : row
                          )
                        );
                      }
                    }}
                  />
                </IconButton>
              </Tooltip>
            );
          }
        };
      }

      // Date
      if (colType === 'date') {
        return {
          ...col,
          editable: true,
          type: 'date',
          valueGetter: (params) => {
            if (!params.value) return null;
            if (params.value instanceof Date) return params.value;
            const d = new Date(params.value);
            return isNaN(d.getTime()) ? null : d;
          },
          valueFormatter: (params) => {
            if (!params.value) return '';
            const d = params.value instanceof Date ? params.value : new Date(params.value);
            return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB');
          }
        };
      }
      // Number
      if (colType === 'number') {
        return {
          ...col,
          editable: true,
          type: 'number',
          valueParser: (value) => Number(value)
        };
      }
      // Default
      return { ...col, editable: true };
    })
  ];

  const getRowClassNameInternal = (params) => {
    if (highLightedRow && typeof highLightedRow === "function" && highLightedRow(params.row)) {
      return 'red-row';
    }
    return '';
  };

  const pageSize = 10;
  const startRecord = rows.length === 0 ? 0 : (paginationCount - 1) * pageSize + 1;
  const endRecord = rows.length === 0 ? rows.length : Math.min(paginationCount * pageSize, totalRecord);

  // Define handlers BEFORE JSX usage
  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handlePageChange = (page) => {
    if (page !== "..." && page !== paginationCount) {
      handlePagination(page);
    }
  };

 // Track edited rows
 const processRowUpdate = async (newRow, oldRow) => {
  try {
    // Validate required row structure
    if (!newRow) {
      return oldRow || {};
    }

    // Use the same getRowIdFromRow function for consistency
    const getRowIdValue = getRowIdFromRow;

    // Get IDs for both rows
    const newRowId = getRowIdValue(newRow);
    const oldRowId = getRowIdValue(oldRow);
    
    // CRITICAL: If we can't identify either row, return oldRow immediately
    // This prevents DataGrid from trying to update a row it can't track
    // DataGrid expects oldRow to be returned when update can't be processed
    if (!newRowId && !oldRowId) {
      // If oldRow exists, return it immediately (DataGrid will revert)
      // This is the safest option - DataGrid knows how to handle oldRow
      if (oldRow) {
        // Ensure oldRow has at least an id field set to prevent DataGrid errors
        if (oldRow.id === undefined || oldRow.id === null) {
          // Try to set id from any available field
          const possibleId = oldRow._id || oldRow.case_id || oldRow.sl_no || oldRow.progress_id || oldRow.property_id || oldRow.fir_id || oldRow.unique_id;
          if (possibleId != null) {
            return { ...oldRow, id: possibleId };
          }
        }
        return oldRow;
      }
      
      // If no oldRow, try to find the row in current rows array
      const currentRows = rowsRef.current || [];
      if (currentRows.length > 0) {
        // Try to match by any available field
        const foundRow = currentRows.find(r => {
          const rId = getRowIdValue(r);
          if (!rId) return false;
          
          // Match by comparing available fields in newRow
          const newRowKeys = Object.keys(newRow).filter(k => k !== 'isNew' && k !== 'id');
          if (newRowKeys.length === 0) return false;
          
          // Check if at least one field matches
          return newRowKeys.some(key => {
            if (newRow[key] != null && r[key] === newRow[key]) {
              return true;
            }
            return false;
          });
        });
        
        if (foundRow) {
          const foundId = getRowIdValue(foundRow);
          if (foundId) {
            // Use the found row and update it with newRow changes
            const mergedRow = { ...foundRow, ...newRow, id: foundId, isNew: false };
            return mergedRow;
          }
        }
      }
      
      // Last resort: return newRow with a temporary id to prevent errors
      if (newRow) {
        return { ...newRow, id: newRow.id || `temp-${Date.now()}` };
      }
      
      // Absolute last resort
      return { id: `temp-${Date.now()}` };
    }

    // If we have oldRow with ID, use it
    let resolvedOldRow = oldRow;
    if (!resolvedOldRow || !oldRowId) {
      const currentRows = rowsRef.current || [];
      // Try to find by newRowId
      if (newRowId) {
        const found = currentRows.find(r => getRowIdValue(r) === newRowId);
        if (found) {
          resolvedOldRow = found;
        }
      }
      
      // If still not found, use oldRow or newRow
      if (!resolvedOldRow) {
        resolvedOldRow = oldRow || newRow;
      }
    }

    // Merge oldRow + newRow
    let mergedRow = { ...resolvedOldRow, ...newRow, isNew: false };

    // Ensure mergedRow has a valid id
    const mergedRowId = getRowIdValue(mergedRow);
    if (!mergedRowId) {
      // Try to get ID from resolvedOldRow or newRow
      const possibleId = getRowIdValue(resolvedOldRow) || getRowIdValue(newRow);
      if (possibleId) {
        mergedRow.id = possibleId;
      } else {
        // Still no ID - return oldRow to prevent errors
        return oldRow || newRow || {};
      }
    } else if (!mergedRow.id) {
      mergedRow.id = mergedRowId;
    }

    // Final validation
    const finalId = getRowIdValue(mergedRow);
    if (!finalId) {
      // Return oldRow to revert - this prevents DataGrid errors
      return oldRow || newRow || {};
    }

    // Additional check: ensure the row exists in current rows (important during pagination)
    const currentRows = rowsRef.current || [];
    const rowExists = currentRows.some(r => {
      const rId = getRowIdValue(r);
      return rId === finalId;
    });

    // If row doesn't exist in current view (e.g., paginated away), 
    // return the oldRow to prevent DataGrid from trying to update a non-existent row
    if (!rowExists && oldRow && getRowIdValue(oldRow)) {
      return oldRow;
    }

    // Apply transformations based on column type
    propColumns.forEach(col => {
      const colType = getColType(col);

      if (
        (colType === "autocomplete" || colType === "singleSelect") &&
        getValueOptions(col)
      ) {
        mergedRow[col.field] = getDropdownValue(col, mergedRow[col.field]);
      }

      if (colType === "date" && mergedRow[col.field] instanceof Date) {
        mergedRow[col.field] = mergedRow[col.field].toISOString().split("T")[0];
      }
    });

    console.log(
      "processRowUpdate: final merged row returned =>",
      mergedRow.id,
      mergedRow
    );

    // Use the final validated id for updates
    const finalMergedId = getRowIdValue(mergedRow);

    // Update rows state - match by id using getRowId if provided
    setRows(prev => {
      const updated = prev.map(row => {
        const rowId = getRowIdValue(row);
        return rowId === finalMergedId ? mergedRow : row;
      });
      rowsRef.current = updated; // Update ref
      return updated;
    });

    // Track edited rows
    setEditedRows(prev => ({
      ...prev,
      [finalMergedId]: mergedRow
    }));

    // Backend update
    if (onRowUpdate) {
      await onRowUpdate(mergedRow, tableName);
    }

    // Ensure returned row has the id in the expected format
    if (!mergedRow.id && finalMergedId) {
      mergedRow.id = finalMergedId;
    }

    return mergedRow; // MUST return complete row with valid id
  } catch (err) {
    console.error("❌ Error in processRowUpdate:", err, { newRow, oldRow });
    // Ensure we return a valid row even on error
    // Helper to get row id (same as above but inline for error handling)
    const getRowIdValue = (row) => {
      if (!row) return null;
      if (getRowId && typeof getRowId === 'function') {
        try {
          return getRowId(row);
        } catch (e) {
          return null;
        }
      }
      return row.id || row._id || row.case_id || row.sl_no || row.progress_id || row.property_id || row.fir_id || row.unique_id || null;
    };
    
    // Prefer oldRow as it's what DataGrid expects on error
    let fallbackRow = oldRow;
    if (!fallbackRow) {
      fallbackRow = newRow;
    }
    
    // If we have a fallback row, ensure it has an id field
    if (fallbackRow) {
      const fallbackId = getRowIdValue(fallbackRow);
      if (fallbackId) {
        // Ensure id field is set
        if (!fallbackRow.id) {
          return { ...fallbackRow, id: fallbackId };
        }
        return fallbackRow;
      } else {
        // Try to extract id from any field
        const possibleId = fallbackRow._id || fallbackRow.case_id || fallbackRow.sl_no || fallbackRow.progress_id || fallbackRow.property_id || fallbackRow.fir_id || fallbackRow.unique_id;
        if (possibleId != null) {
          return { ...fallbackRow, id: possibleId };
        }
        // If still no id, set a temporary one to prevent DataGrid errors
        return { ...fallbackRow, id: `temp-${Date.now()}` };
      }
    }
    
    // Absolute last resort - return an object with a temporary id
    return { id: `temp-${Date.now()}` };
  }
};


  

  // Debug log - can be removed in production
  // console.log("Incoming row IDs:", propRows.map(r => r.case_id ?? r.id ?? r._id ?? r.sl_no));

  React.useImperativeHandle(
    ref,
    () => ({
      getRows: () => {
        const mappedRows = rows.map(row => {
          const updatedRow = { ...row };
          propColumns.forEach(col => {
            const colType = getColType(col);
            if ((colType === 'autocomplete' || colType === 'singleSelect') && getValueOptions(col)) {
              updatedRow[col.field] = getDropdownValue(col, updatedRow[col.field]);
            }
            if (colType === 'date' && updatedRow[col.field] instanceof Date) {
              updatedRow[col.field] = updatedRow[col.field].toISOString().split('T')[0];
            }
          });
          return updatedRow;
        });
        console.log("getRows called, returning rows:", mappedRows);
        return mappedRows;
      },
      commitAllEdits: async () => {
        console.log("commitAllEdits called");
        setRowModesModel((prev) => {
          const newModel = { ...prev };
          rows.forEach(row => {
            newModel[row.id] = { mode: GridRowModes.View };
          });
          return newModel;
        });
        await new Promise(resolve => setTimeout(resolve, 0));
        return new Promise(resolve => {
          setTimeout(() => {
            const mappedRows = rows.map(row => {
              const updatedRow = { ...row };
              propColumns.forEach(col => {
                const colType = getColType(col);
                if ((colType === 'autocomplete' || colType === 'singleSelect') && getValueOptions(col)) {
                  updatedRow[col.field] = getDropdownValue(col, updatedRow[col.field]);
                }
                if (colType === 'date' && updatedRow[col.field] instanceof Date) {
                  updatedRow[col.field] = updatedRow[col.field].toISOString().split('T')[0];
                }
              });
              return updatedRow;
            });
            console.log("commitAllEdits returning rows:", mappedRows);
            resolve([...mappedRows]);
          }, 0);
        });
      }
    }),
    [rows, propColumns, getColType, getValueOptions, getDropdownValue]
  );
  return (
    <Box sx={{ margin: "6px" }}>
      <Paper sx={{ width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={updatedColumns}
          getRowId={getRowIdFromRow}
          rowHeight={45}
           sx={{
            '& .red-row': {
              backgroundColor: '#ffe5e5',
            },
            '& .red-row.MuiDataGrid-row--editing': {
              backgroundColor: '#ffe5e5',
            },
            '& .red-row .MuiDataGrid-cell': {
              backgroundColor: '#ffe5e5',
            },
            '& .MuiDataGrid-cell--editing': {
              backgroundColor: 'transparent',
            },
          }}
          className={`FigmaTableView ${tableName && (tableName === "cid_ui_case_accused" || tableName === "cid_pt_case_witness") ? 'excelViewTable' : ''}`}
          disableColumnMenu
          disableColumnSorting
          hideFooter
          checkboxSelection={checkboxSelection}
          disableRowSelectionOnClick
          stickyHeader
          editMode="row"
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          onRowEditStop={handleRowEditStop}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={(error) => console.error("Update error:", error)}
          onRowClick={(params) => handleRowClick && handleRowClick(params.row)}
          getRowClassName={getRowClassNameInternal}
          experimentalFeatures={{ newEditingApi: true }}
        />
      </Paper>

      { handlePagination && paginationCount ?
        <Box sx={{display:'flex',justifyContent:'space-between'}} pt={2}>
            <>
                <p style={{fontSize:'14px'}}>
                    {startRecord} - {endRecord} of {totalRecord}
                </p>
                <Stack spacing={2} direction="row" justifyContent="center">
                    <Pagination
                        count={totalPage}
                        page={paginationCount}
                        onChange={(event, page) => handlePageChange(page)}
                        renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            onClick={() => handlePageChange(item.page)}
                            disabled={item.page === "..." || (item.type === "previous" && paginationCount === 1) || (item.type === "next" && (rows.length === 0 || paginationCount === totalPage))}
                            sx={{
                                mx: 0.5,
                                cursor: item.page === "..." ? "default" : "pointer",
                                backgroundColor: paginationCount === item.page ? "#1976d2" : "transparent",
                                color: paginationCount === item.page ? "#fff" : "inherit",
                            }}
                        />
                        )}
                    />
                </Stack>
            </>
        </Box>
        : 
        <Box sx={{display:'flex',justifyContent:'space-between'}} pt={2}>
            {handleBack && handleNext && 
                <>
                    <Button disabled={!backBtn} onClick={handleBack} sx={{ textTransform: 'none',color:'#1D2939',border:'1px solid #D0D5DD',borderRadius:'6px',fontSize:'14px',fontWeight:'500' }} className='Roboto'>
                        <ArrowBackIcon />
                        Back
                    </Button>
                    <Button disabled={!nextBtn} onClick={handleNext} sx={{ textTransform: 'none',color:'#1D2939',border:'1px solid #D0D5DD',borderRadius:'6px',fontSize:'14px',fontWeight:'500' }} className='Roboto'>
                        Next
                        <ArrowForwardIcon />
                    </Button>
                </>        
            }
        </Box>
      }
    </Box>
  );
});

export default EditTableView;
